import {
	DocumentLineageInfo,
	parseDocumentLineageInfo,
} from '@/entities/document-lineage';

export type DocumentLineageResult =
	| { status: 'ok'; info: DocumentLineageInfo }
	| { status: 'not-found' }
	| { status: 'error' };

// Public, unauthenticated lookup behind the QR of a signed document.
//
// This deliberately does not go through `http`/`rawHttp`: that client logs the
// user out and redirects to `/` on a 401/403, which would be wrong on a page
// meant to work logged-out, and it funnels failures into shared logging. The
// access token `k` must never be logged, reported to Honeybadger or sent to
// analytics, so nothing here echoes the URL on failure. The same holds for the
// presigned `download_url` in the response: it grants the signed PDF to whoever
// holds it until it expires.
export async function getDocumentLineageInfo(
	ulid: string,
	token: string,
): Promise<DocumentLineageResult> {
	const baseUrl: string = import.meta.env.VITE_API_URL ?? '';
	const url = `${baseUrl.replace(/\/+$/, '')}/d/${encodeURIComponent(ulid)}?k=${encodeURIComponent(token)}`;

	let response: Response;
	try {
		response = await fetch(url, {
			method: 'GET',
			headers: { Accept: 'application/json' },
		});
	} catch {
		return { status: 'error' };
	}

	if (response.status === 404) {
		return { status: 'not-found' };
	}

	if (!response.ok) {
		return { status: 'error' };
	}

	let body: unknown;
	try {
		body = await response.json();
	} catch {
		return { status: 'error' };
	}

	// A 200 that does not match the contract is treated as an error rather than
	// rendered half-empty: the card is a verification surface.
	const info = parseDocumentLineageInfo(body);
	if (!info) {
		return { status: 'error' };
	}

	return { status: 'ok', info };
}
